// ─────────────────────────────────────────────────────────────────────────────
// AI STUDIO PAGE — rendering of the 'ai-studio-page' block (one HTML document
// designed by the AI, see apps/api/src/ai/studio/studio-page.ts).
//
// data: { html, textOverrides, accent?, bg?, text? }
//  • textOverrides use the same keys as the qwen-draft monolithic pages edited by
//    FullPageIframeBlock: "<key>" (text), "<key>__src" (image), "<key>__href",
//    "<key>__pos", "<key>__ff", "<key>__fs".
//  • accent / bg / text override the document's CSS variables.
// ─────────────────────────────────────────────────────────────────────────────

type D = Record<string, unknown>

const escText = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
const escAttr = (s: string) => escText(s).replace(/"/g, '&quot;')
const isHex = (s: unknown): s is string => typeof s === 'string' && /^#[0-9a-f]{3,8}$/i.test(s.trim())
const reKey = (k: string) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** Allow only harmless inline markup inside an overridden text. */
function safeInline(v: string): string {
  if (!/[<>]/.test(v)) return escText(v)
  return escText(v)
    .replace(/&lt;br\s*\/?&gt;/gi, '<br>')
    .replace(/&lt;(\/?)(em|b|strong|i|span)&gt;/gi, '<$1$2>')
}

function lum(hex: string): number {
  let h = hex.replace('#', '').slice(0, 6)
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  return (parseInt(h.slice(0, 2), 16) * 299 + parseInt(h.slice(2, 4), 16) * 587 + parseInt(h.slice(4, 6), 16) * 114) / 1000
}

function colorVarsCss(d: D): string {
  const v: string[] = []
  if (isHex(d.accent)) { v.push(`--accent:${d.accent}`, `--on-accent:${lum(d.accent) > 150 ? '#111' : '#fff'}`) }
  if (isHex(d.bg)) v.push(`--bg:${d.bg}`)
  if (isHex(d.text)) v.push(`--text:${d.text}`)
  return v.length ? `<style data-bst-vars>:root,html[data-theme]{${v.join(';')}}</style>` : ''
}

function insertIntoHead(html: string, snippet: string): string {
  if (!snippet) return html
  const i = html.search(/<\/head>/i)
  return i >= 0 ? html.slice(0, i) + snippet + html.slice(i) : snippet + html
}

/** Apply text and image overrides server-side (works without JS, good for SEO). */
export function applyStudioOverrides(html: string, overrides: Record<string, string>): string {
  let s = html
  for (const [key, value] of Object.entries(overrides ?? {})) {
    if (typeof value !== 'string') continue
    if (key.endsWith('__src')) {
      const k = reKey(key.slice(0, -5))
      if (!/^(https?:\/\/|\/|data:image\/)/i.test(value.trim())) continue
      s = s.replace(new RegExp(`<img\\b([^>]*\\bdata-img="${k}"[^>]*)>`, 'i'), (_m, attrs: string) =>
        `<img${attrs.replace(/\ssrc\s*=\s*("[^"]*"|'[^']*')/i, '')} src="${escAttr(value)}">`)
      continue
    }
    if (/__(href|pos|ff|fs)$/.test(key) || key.startsWith('__')) continue // runtime script handles these
    const k = reKey(key)
    // <tag … data-t="key" …>CONTENT</tag>  (contract: no nested same-name tags)
    s = s.replace(new RegExp(`(<([a-z][a-z0-9]*)\\b[^>]*\\b(?:data-t|data-bind|id)="${k}"[^>]*>)([\\s\\S]*?)(</\\2>)`, 'i'),
      (_m, open: string, _tag: string, _old: string, close: string) => `${open}${safeInline(value)}${close}`)
  }
  return s
}

/** Small runtime for the overrides that need the DOM (links, crop, per-element fonts). */
function runtimeOverridesScript(overrides: Record<string, string>): string {
  const rest: Record<string, string> = {}
  for (const [k, v] of Object.entries(overrides ?? {})) if (/__(href|pos|ff|fs)$/.test(k) && typeof v === 'string') rest[k] = v
  if (!Object.keys(rest).length) return ''
  return `<script>(function(){var o=${JSON.stringify(rest).replace(/</g, '\\u003c')};function q(k){return document.querySelector('[data-t="'+k+'"],[data-bind="'+k+'"],img[data-img="'+k+'"]')||document.getElementById(k)}Object.keys(o).forEach(function(id){var v=o[id],m=id.match(/^(.*)__(href|pos|ff|fs)$/);if(!m)return;var el=q(m[1]);if(!el)return;if(m[2]==='href'){var a=el.closest('a');if(a&&/^(https?:|\\/|#|mailto:|tel:)/.test(v))a.setAttribute('href',v)}else if(m[2]==='pos'){el.style.objectFit='cover';el.style.objectPosition=v}else if(m[2]==='ff'){el.style.fontFamily="'"+v.replace(/['"<>]/g,'')+"',system-ui,sans-serif";var l=document.createElement('link');l.rel='stylesheet';l.href='https://fonts.googleapis.com/css2?family='+encodeURIComponent(v).replace(/%20/g,'+')+':wght@400;600;700&display=swap';document.head.appendChild(l)}else if(m[2]==='fs'){el.style.fontSize=v.replace(/[^0-9a-z.%()+\\-*/ ]/gi,'')}})})()</script>`
}

/**
 * HTML of an ai-studio-page block.
 *  mode 'site'   — published page: overrides applied, scripts kept.
 *  mode 'canvas' — editor iframe: scripts removed (the AI document must not run
 *                  inside the editor origin), reveal animations forced visible;
 *                  text overrides are applied live by FullPageIframeBlock.
 */
export function studioPageHtml(data: D, mode: 'site' | 'canvas' = 'site'): string {
  const raw = String(data.html ?? '')
  if (!raw.trim()) {
    return `<div style="padding:80px 24px;text-align:center;font-family:system-ui;color:#888">AI-страница пуста — сгенерируйте сайт в режиме «Студия».</div>`
  }
  let html = insertIntoHead(raw, colorVarsCss(data))
  if (mode === 'canvas') {
    html = html.replace(/<script\b[\s\S]*?<\/script>/gi, '')
    html = html.replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*')/gi, '')
    html = html.replace(/(href|src|action)\s*=\s*(["'])\s*javascript:[^"']*\2/gi, '$1=$2#$2')
    html = insertIntoHead(html, '<style>[data-reveal],.reveal{opacity:1!important;transform:none!important;filter:none!important}</style>')
    return html
  }
  const overrides = (data.textOverrides ?? {}) as Record<string, string>
  html = applyStudioOverrides(html, overrides)
  const rt = runtimeOverridesScript(overrides)
  if (rt) {
    const i = html.search(/<\/body>/i)
    html = i >= 0 ? html.slice(0, i) + rt + html.slice(i) : html + rt
  }
  return html
}
